import clsx from 'clsx';
import { useAuth } from 'context/auth';
import Link from 'next/link';
import { CSSProperties, useMemo, useState } from 'react';

import { Icon } from 'common/icon';
import { ActionBlockButton } from 'components/action-block';

import { IStreamer } from 'constants/types';

import { LoginModal } from 'components/modal/login';
import { Anchor } from 'constants/anchors';
import { Routes } from 'constants/routes';
import { useLoginModal, useRedirectActions } from 'hooks';
import { useFavorites } from 'hooks/useFavorites';
import { useMeQuery } from 'hooks/api/users';
import { useVariablesByNameQuery } from 'hooks/api/variables';
import { formatPluralWord } from 'utils/formats';
import { getItemIdentifier } from 'utils/slug';

import { StreamerReviewModal } from 'components/modal/streamer-review';
import { StreamerCard } from 'components/streamer-card';
import { Analytics } from 'constants/analytics';
import { RedirectActions } from 'constants/redirect-actions';
import { saveRedirectOptions } from 'utils/redirectOptions';
import { HeaderLinks } from './links';
import styles from './styles.module.css';
import { useRouter } from 'next/router';

export const StreamerHeader = ({ data }: { data: IStreamer }) => {
  const router = useRouter();

  const [isReviewVisible, setReviewVisible] = useState(false);

  const { isLoggedIn } = useAuth();

  const { data: { user } = {}, isFetching: isUserFetching } = useMeQuery();
  const { addToFavorite, removeFromFavorite, isLoading } = useFavorites();
  const { data: { value: linkChooseCasino } = { value: '' } } =
    useVariablesByNameQuery({
      name: 'link-choose-casino',
    });

  const isFavorite = useMemo(() => {
    if (
      user?.favorite_streamers?.find((item) => item._id === data?._id) &&
      user?.favorite_streamers?.length
    ) {
      return true;
    }

    return false;
  }, [data?._id, user]);

  const onFavoriteText = useMemo(() => {
    if (isLoading || isUserFetching) {
      return 'Loading...';
    }
    if (isFavorite) {
      return 'Remove';
    } else {
      return 'Save';
    }
  }, [isFavorite, isLoading, isUserFetching]);

  const {
    handleCloseLoginModal,
    handleConfirmLoginModal,
    handleVisibleLoginModal,
    isLoginVisible,
  } = useLoginModal();

  useRedirectActions({
    actions: [
      {
        name: RedirectActions.Favorite,
        action: () => addToFavorite({ streamerId: data?._id }),
      },
    ],
  });

  const backgroundStyle: CSSProperties = {
    background: `url(${data?.imageUrl}) top/cover no-repeat`,
  };

  return (
    <div>
      <div className={styles.container_background} style={backgroundStyle} />
      <div className={styles.container_s}>
        <div className={styles.streamer_block__row}>
          <div className={styles.streamer_block__card}>
            <StreamerCard item={data} variant="user" isLink={false} />
          </div>
          <div className={styles.streamer_block__info}>
            <div className={styles.streamer__title}>
              <h1>{data?.titlePage}</h1>
            </div>
            <ActionBlockButton
              data={[
                {
                  id: isFavorite
                    ? Analytics.casino.unsave
                    : Analytics.casino.save,
                  anchorId: 0,
                  text: onFavoriteText,
                  icon: isFavorite ? 'remove' : 'save',
                  onClick: async () => {
                    if (isLoggedIn) {
                      if (isFavorite) {
                        await removeFromFavorite({ favoriteId: data?._id });
                      } else {
                        await addToFavorite({ streamerId: data?._id });
                      }
                    } else {
                      saveRedirectOptions({
                        url: Routes.streamer(getItemIdentifier(data)),
                        action: RedirectActions.Favorite,
                      });
                      handleVisibleLoginModal();
                    }
                  },
                },
                {
                  id: Analytics.casino.inspect,
                  anchorId: Anchor.CasinoReviews,
                  text: 'Inspect',
                  icon: 'inspect',
                  onClick: () => {
                    if (isLoggedIn) {
                      setReviewVisible(true);
                    } else {
                      saveRedirectOptions({
                        url: Routes.streamer(getItemIdentifier(data)),
                        action: RedirectActions.WriteCasinoReview,
                      });
                      handleVisibleLoginModal();
                    }
                  },
                },
                {
                  id: Analytics.casino.allInspections,
                  anchorId: null,
                  text: formatPluralWord({
                    number: data?.reviews_count ?? 0,
                    word: 'inspection',
                  }),
                  icon: 'search',
                },
              ]}
            />
            <HeaderLinks data={data} />
            <Link
              id={Analytics.casino.howToChoose}
              href={linkChooseCasino ?? ''}
              className={clsx(styles.streamer_question_container)}
            >
              <Icon.ReadPost />
              <span className={styles.streamer_question_text}>
                Why is it profitable to play with streamers?
              </span>
              <Icon.Arrow width={20} height={20} />
            </Link>
            <Link
              id={Analytics.casino.howToChoose}
              href={linkChooseCasino ?? ''}
              className={clsx(styles.streamer_question_container)}
            >
              <Icon.ReadPost />
              <span className={styles.streamer_question_text}>
                How can streamers cheat players
              </span>
              <Icon.Arrow width={20} height={20} />
            </Link>
          </div>
        </div>
      </div>

      {'username' in data && (
        <StreamerReviewModal
          onSuccess={() => router.reload()}
          streamer={data as IStreamer}
          isVisible={isReviewVisible}
          setVisible={setReviewVisible}
        />
      )}

      <LoginModal
        isVisible={isLoginVisible}
        onClose={handleCloseLoginModal}
        confirmOnClick={handleConfirmLoginModal}
      />
    </div>
  );
};
